import { useMemo } from "react";
import {
  FeatureRequestStatus,
  REQUEST_STATUS_META,
} from "../../../api/featureRequests";
import {
  useFeatureRequestsQuery,
  useProductFeaturesQuery,
} from "../hooks/useFeatureRequestsQueries";
import FeatureAutocomplete from "./FeatureAutocomplete";
import RequestsTable from "./RequestsTable";
import DSBox from "../../system-design/DSBox";
import DSAlert from "../../system-design/DSAlert";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSEnumSelect from "../../system-design/DSEnumSelect";
import DSTextField from "../../system-design/DSTextField";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";

export const ALL_STATUSES = "all";
export type RequestStatusFilter = FeatureRequestStatus | typeof ALL_STATUSES;

export type RequestFilters = {
  search: string;
  status: RequestStatusFilter;
  featureId: string | null;
};

export const DEFAULT_REQUEST_FILTERS: RequestFilters = {
  search: "",
  status: ALL_STATUSES,
  featureId: null,
};

const RequestsTab = (props: {
  filters: RequestFilters;
  onFiltersChange: (filters: RequestFilters) => void;
}) => {
  const { filters, onFiltersChange } = props;
  const { data: requests, isLoading, isError } = useFeatureRequestsQuery();
  const { data: features = [] } = useProductFeaturesQuery();

  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    (requests ?? []).forEach((r) => {
      counts[r.status] = (counts[r.status] ?? 0) + 1;
    });
    return counts;
  }, [requests]);

  const statusOptions: { value: RequestStatusFilter; label: string }[] = [
    { value: ALL_STATUSES, label: `All statuses (${requests?.length ?? 0})` },
    ...Object.values(FeatureRequestStatus).map((status) => ({
      value: status,
      label: `${REQUEST_STATUS_META[status].label} (${countByStatus[status] ?? 0})`,
    })),
  ];

  const rows = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return (requests ?? []).filter(
      (r) =>
        (filters.status === ALL_STATUSES || r.status === filters.status) &&
        (!filters.featureId || r.featureId === filters.featureId) &&
        (!query ||
          [r.customerEmail, r.customerName, r.notes, r.featureTitle].some(
            (value) => value?.toLowerCase().includes(query),
          )),
    );
  }, [requests, filters]);

  const selectedFeature = features.find((f) => f.id === filters.featureId);
  const pendingReview =
    countByStatus[FeatureRequestStatus.PotentiallyResolved] ?? 0;
  const isFiltered =
    filters.search !== "" ||
    filters.status !== ALL_STATUSES ||
    filters.featureId !== null;

  if (isLoading) {
    return (
      <DSBox sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <DSCircularProgress />
      </DSBox>
    );
  }
  if (isError) {
    return (
      <DSAlert severity="error">
        Failed to load requests. Please try again.
      </DSAlert>
    );
  }

  return (
    <>
      {pendingReview > 0 &&
        filters.status !== FeatureRequestStatus.PotentiallyResolved && (
          <DSAlert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <DSSecondaryButton
                size="small"
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    status: FeatureRequestStatus.PotentiallyResolved,
                  })
                }
              >
                Review
              </DSSecondaryButton>
            }
          >
            {pendingReview} request(s) are potentially resolved by a deployed
            feature and waiting for review.
          </DSAlert>
        )}

      <DSBox sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <DSTextField
          size="small"
          placeholder="Search email, name, notes or feature..."
          value={filters.search}
          onChange={(e) =>
            onFiltersChange({ ...filters, search: e.target.value })
          }
          sx={{ flex: 2, minWidth: 220 }}
        />
        <DSEnumSelect<RequestStatusFilter>
          size="small"
          value={filters.status}
          options={statusOptions}
          onChange={(status) => onFiltersChange({ ...filters, status })}
          sx={{ minWidth: 240 }}
        />
        <DSBox sx={{ flex: 1, minWidth: 220 }}>
          <FeatureAutocomplete
            features={features}
            value={
              selectedFeature
                ? { kind: "existing", feature: selectedFeature }
                : null
            }
            onChange={(choice) =>
              onFiltersChange({
                ...filters,
                featureId:
                  choice?.kind === "existing" ? choice.feature.id : null,
              })
            }
            label="Filter by feature"
            size="small"
          />
        </DSBox>
        {isFiltered && (
          <DSSecondaryButton
            onClick={() => onFiltersChange(DEFAULT_REQUEST_FILTERS)}
          >
            Clear filters
          </DSSecondaryButton>
        )}
      </DSBox>

      <RequestsTable
        requests={rows}
        features={features}
        showFeature
        emptyText={
          requests?.length
            ? "No requests match the current filters."
            : "No feature requests yet. Click “Add request” to log the first one."
        }
      />
    </>
  );
};

export default RequestsTab;
